package com.bulut.mecitogullari.core.export

import com.bulut.mecitogullari.core.i18n.APP_NAME
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.tenant.CompanyProfile
import com.bulut.mecitogullari.core.util.formatAmountPrecise
import com.bulut.mecitogullari.core.util.formatDate
import com.bulut.mecitogullari.core.util.todayUtcMidnightMillis
import com.bulut.mecitogullari.data.model.AccountKind
import com.bulut.mecitogullari.data.model.DebtEntry
import com.bulut.mecitogullari.data.model.DebtEntryType
import com.bulut.mecitogullari.data.model.Supplier
import kotlin.math.roundToLong

/**
 * Printable supplier account statement ("Cari Hesap Ekstresi").
 * The page auto-opens the print dialog, where the user saves it as PDF.
 */
object DebtStatementHtml {

    fun build(supplier: Supplier, entries: List<DebtEntry>): String {
        val s = Localization.strings
        val isCustomer = supplier.kind == AccountKind.CUSTOMER
        val purchaseHeader = if (isCustomer) s.pdfSale else s.pdfPurchase
        val paymentHeader = if (isCustomer) s.pdfCollection else s.pdfPayment
        val purchaseClass = if (isCustomer) "credit" else "debit"
        val paymentClass = if (isCustomer) "" else "credit"

        val ordered = entries.sortedBy { it.dateEpochMillis }
        val totalPurchases = ordered.filter { it.type == DebtEntryType.PURCHASE }.sumOf { it.amount }
        val totalPayments = ordered.filter { it.type == DebtEntryType.PAYMENT }.sumOf { it.amount }
        val balance = totalPurchases - totalPayments

        val rows = buildString {
            var running = 0.0
            ordered.forEach { entry ->
                val isPurchase = entry.type == DebtEntryType.PURCHASE
                running += if (isPurchase) entry.amount else -entry.amount
                append("<tr>")
                append("<td>${formatDate(entry.dateEpochMillis)}</td>")
                append("<td>${escape(entry.description)}</td>")
                append("<td class=\"num $purchaseClass\">${if (isPurchase) money(entry.amount) else ""}</td>")
                append("<td class=\"num $paymentClass\">${if (!isPurchase) money(entry.amount) else ""}</td>")
                append("<td class=\"num\">${money(running)}</td>")
                append("</tr>")
                entry.items.forEach { item ->
                    append("<tr class=\"item\">")
                    append("<td></td>")
                    append(
                        "<td colspan=\"3\">${escape(item.name)} — ${quantity(item.quantity)} ${escape(item.unit)}" +
                            " × ${money(item.unitPrice)}</td>"
                    )
                    append("<td class=\"num\">${money(item.total)}</td>")
                    append("</tr>")
                }
            }
        }

        return """
<!DOCTYPE html>
<html lang="tr">
<head>
<meta charset="utf-8">
<title>${escape(supplier.name)} - ${s.pdfStatement}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #0F172A; margin: 32px; }
  h1 { font-size: 20px; margin: 0; }
  .subtitle { color: #64748B; font-size: 12px; margin: 4px 0 20px 0; }
  table { width: 100%; border-collapse: collapse; font-size: 12px; }
  th { text-align: left; background: #1E293B; color: #fff; padding: 8px; }
  td { padding: 6px 8px; border-bottom: 1px solid #E2E8F0; }
  .num { text-align: right; white-space: nowrap; }
  .debit { color: #DC2626; }
  .credit { color: #16A34A; }
  tr.item td { color: #64748B; font-size: 11px; border-bottom: 1px dashed #EEF2F7; padding-top: 2px; padding-bottom: 2px; }
  .summary { margin-top: 20px; width: 320px; margin-left: auto; font-size: 13px; }
  .summary td { padding: 6px 8px; }
  .summary .total { font-weight: bold; font-size: 15px; border-top: 2px solid #0F172A; }
  .letterhead {
    display: flex; justify-content: space-between; align-items: baseline;
    border-bottom: 2px solid #0F172A; padding-bottom: 8px; margin-bottom: 18px;
  }
  .letterhead .company { font-size: 15px; font-weight: bold; }
  .letterhead .app { font-size: 11px; color: #64748B; letter-spacing: 2px; }
  @media print { body { margin: 12mm; } }
</style>
</head>
<body>
<div class="letterhead">
<span class="company">${escape(CompanyProfile.name.value.trim())}</span>
<span class="app">$APP_NAME</span>
</div>
<h1>${escape(supplier.name)}</h1>
<p class="subtitle">${s.pdfStatement} • ${formatDate(todayUtcMidnightMillis())}${
            if (supplier.phone.isNotBlank()) " • ${escape(supplier.phone)}" else ""
        }</p>
<table>
<thead>
<tr><th>${s.date}</th><th>${s.description}</th><th class="num">$purchaseHeader</th><th class="num">$paymentHeader</th><th class="num">${s.pdfBalance}</th></tr>
</thead>
<tbody>
$rows
</tbody>
</table>
<table class="summary">
<tr><td>${if (isCustomer) s.pdfTotalSales else s.pdfTotalPurchases}</td><td class="num $purchaseClass">${money(totalPurchases)}</td></tr>
<tr><td>${if (isCustomer) s.pdfTotalCollections else s.pdfTotalPayments}</td><td class="num $paymentClass">${money(totalPayments)}</td></tr>
<tr class="total"><td>${
            if ((balance >= 0) == isCustomer) s.pdfCurrentReceivable else s.pdfCurrentDebt
        }</td><td class="num">${money(kotlin.math.abs(balance))}</td></tr>
</table>
<script>window.onload = function () { window.print(); };</script>
</body>
</html>
        """.trimIndent()
    }

    private fun money(amount: Double): String = formatAmountPrecise(amount)

    private fun quantity(value: Double): String =
        if (value == value.roundToLong().toDouble()) {
            value.roundToLong().toString()
        } else {
            value.toString().replace('.', ',')
        }

    private fun escape(text: String): String = text
        .replace("&", "&amp;")
        .replace("<", "&lt;")
        .replace(">", "&gt;")
}
